/**
 * Budget range bar — Realestate.com.au-style dual-handle price slider.
 *
 * Why this exists:
 *   Users told us the Budget dropdown (nine opaque options in a listbox)
 *   felt clinical. Realestate.com.au, Domain, Zillow all use a visual
 *   price-range bar where you drag Min and Max handles. It turns "pick
 *   a bucket from a menu" into "grab and sweep your budget window on a
 *   scale" — far more intuitive, especially on mobile.
 *
 * What it renders:
 *   ┌─────────────────────────────────────────────┐
 *   │ BUDGET                A$700k – A$1.2M       │  ← header
 *   │  ●────────────●──────────────────           │  ← bar with 2 handles
 *   └─────────────────────────────────────────────┘
 *
 *   The track has tiny tick marks at every snap stop so the discreteness
 *   reads visually ("I can drop the handle only at these points"). The
 *   fill between the handles is amber. Handles are large enough to hit
 *   with a thumb on mobile.
 *
 * How it maps to the BudgetBucket enum:
 *   All snap points come from the bucket lo/hi boundaries for the active
 *   (city, mode, type) context. That gives N≈10 discrete stops. When the
 *   user releases a handle we find the bucket whose [lo,hi] has the
 *   largest overlap with the user's [min,max] range and call
 *   `setBudgetBucket(bucket)`. If the user's range spans the full scale,
 *   bucket = "any". This keeps every downstream consumer (shortlist,
 *   scoring, labels) untouched — the control is the only thing that
 *   changes shape.
 *
 * Accessibility:
 *   - Both handles are focusable <button role="slider">.
 *   - Arrow keys step one snap point; Home/End jump to the bounds.
 *   - `aria-valuemin`, `aria-valuemax`, `aria-valuenow`, `aria-valuetext`
 *     are updated on every move so assistive tech announces the current
 *     price, not an index.
 *   - `touch-action: none` on the bar prevents iOS/Android scroll
 *     hijacking when dragging.
 *
 * Robustness:
 *   - Pointer capture + pointercancel teardown so the drag never sticks
 *     if the OS steals the pointer (incoming call, tab switch, etc.).
 *   - Re-subscribes to PurchaseMode / PropertyType / BudgetBucket so the
 *     bar re-renders the instant any of those context axes change.
 *   - Handles clamp `min < max` at all times (never cross, never equal).
 *   - When context switches and the stored bucket's boundaries no longer
 *     appear in the new stop set, we snap to the nearest available pair.
 */

import {
  getBudgetBucket,
  setBudgetBucket,
  setUserBudgetRange,
  subscribeBudgetBucket,
} from "./budget-band";
import {
  budgetBucketDef,
  BUDGET_BUCKETS,
  type BudgetBucket,
  type BudgetFormatCtx,
  type CityCode,
} from "./budget-format";
import {
  getPurchaseMode,
  subscribePurchaseMode,
} from "./purchase-mode";
import {
  getPropertyType,
  subscribePropertyType,
} from "./property-type";

/* -------------------------------------------------------------------------- */
/*  Per-context helpers                                                       */
/* -------------------------------------------------------------------------- */

/** Open-upper sentinels — mirror budget-format.ts. Values represent the
 *  rightmost snap point in each unit system. */
function openUpperFor(ctx: BudgetFormatCtx): number {
  if (ctx.city === "mel") return ctx.mode === "buy" ? 5000 : 1800;
  if (ctx.city === "syd") return ctx.mode === "buy" ? 8000 : 2500;
  // hyd & blr share Indian-rupee caps.
  return ctx.mode === "buy" ? 700 : 180;
}

/** Unique sorted list of bucket lo/hi boundaries for the active context.
 *  Excludes the "any" bucket — that's handled as a special selection. */
function getStops(ctx: BudgetFormatCtx): number[] {
  const set = new Set<number>();
  for (const b of BUDGET_BUCKETS) {
    if (b === "any") continue;
    const def = budgetBucketDef(ctx, b);
    set.add(def.lo);
    set.add(def.hi);
  }
  return Array.from(set).sort((a, b) => a - b);
}

/** Best-fit bucket for a user-selected [lo,hi] window:
 *   1. Full-scale window → "any".
 *   2. Exact [lo,hi] match → that bucket.
 *   3. Otherwise the bucket with maximum numeric overlap.
 */
function bestBucketForRange(
  ctx: BudgetFormatCtx,
  loVal: number,
  hiVal: number
): BudgetBucket {
  const stops = getStops(ctx);
  const first = stops[0];
  const last = stops[stops.length - 1];
  if (loVal <= first && hiVal >= last) return "any";

  // Exact boundary match → deterministic pick.
  for (const b of BUDGET_BUCKETS) {
    if (b === "any") continue;
    const def = budgetBucketDef(ctx, b);
    if (def.lo === loVal && def.hi === hiVal) return b;
  }

  // Best overlap fallback. Tie-breaker: narrower bucket wins (keeps the
  // shortlist tighter and closer to the user's intent).
  let bestB: BudgetBucket = "any";
  let bestOverlap = -1;
  let bestWidth = Infinity;
  for (const b of BUDGET_BUCKETS) {
    if (b === "any") continue;
    const def = budgetBucketDef(ctx, b);
    const ov = Math.max(0, Math.min(hiVal, def.hi) - Math.max(loVal, def.lo));
    const width = def.hi - def.lo;
    if (ov > bestOverlap || (ov === bestOverlap && width < bestWidth)) {
      bestOverlap = ov;
      bestWidth = width;
      bestB = b;
    }
  }
  return bestB;
}

/** Handle positions (stop indices) that represent the given bucket. Used
 *  to sync the bar to a bucket that was set programmatically or from
 *  localStorage. */
function indicesForBucket(
  ctx: BudgetFormatCtx,
  bucket: BudgetBucket
): { minIdx: number; maxIdx: number } {
  const stops = getStops(ctx);
  if (bucket === "any") {
    return { minIdx: 0, maxIdx: stops.length - 1 };
  }
  const def = budgetBucketDef(ctx, bucket);
  const mi = stops.indexOf(def.lo);
  const ma = stops.indexOf(def.hi);
  if (mi < 0 || ma < 0 || ma <= mi) {
    // Context flipped (e.g. Buy → Rent) and the stored bucket no longer
    // has matching stops — snap to the first available bucket.
    return { minIdx: 0, maxIdx: 1 };
  }
  return { minIdx: mi, maxIdx: ma };
}

/* -------------------------------------------------------------------------- */
/*  Formatter — stop number → user-visible label                              */
/* -------------------------------------------------------------------------- */

/** Format a single stop value per context. Used for handle labels and the
 *  header range text. Always includes currency prefix — callers render the
 *  separator ("–" or "+") between labels. */
function formatStop(ctx: BudgetFormatCtx, n: number): string {
  // Australian cities (mel, syd) — A$ for buy, $/wk for rent.
  if (ctx.city === "mel" || ctx.city === "syd") {
    if (ctx.mode === "rent") return `$${Math.round(n)}/wk`;
    if (n <= 0) return "A$0";
    if (n < 1000) return `A$${Math.round(n)}k`;
    const m = n / 1000;
    const s = Number.isInteger(m) ? m.toFixed(0) : m.toFixed(1);
    return `A$${s}M`;
  }
  // Indian cities (hyd, blr) — ₹ Lakh for buy, ₹ k/mo for rent.
  if (ctx.mode === "rent") {
    if (n <= 0) return "₹0";
    return `₹${Math.round(n)}k/mo`;
  }
  if (n <= 0) return "₹0";
  if (n < 100) return `₹${Math.round(n)}L`;
  const cr = n / 100;
  const s = Number.isInteger(cr) ? cr.toFixed(0) : cr.toFixed(1);
  return `₹${s}Cr`;
}

/** Max-handle label. When the handle sits on the open-upper sentinel,
 *  show the previous stop + "+" (e.g. "A$2M+") — same convention as
 *  budget-format.ts. */
function formatMax(ctx: BudgetFormatCtx, n: number, atLastStop: boolean): string {
  const openU = openUpperFor(ctx);
  if (atLastStop || n >= openU) {
    const stops = getStops(ctx);
    const prev = stops[stops.length - 2];
    return `${formatStop(ctx, prev)}+`;
  }
  return formatStop(ctx, n);
}

/* -------------------------------------------------------------------------- */
/*  Component                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Build the budget range bar. Returns a single HTMLElement ready to mount
 * anywhere (top-rail on desktop, filter-page slot on mobile). All state is
 * global (BudgetBucket / PurchaseMode / PropertyType) so multiple mounts
 * would stay synchronised — but in practice only one is mounted at a time.
 */
export function buildBudgetRangeBar(city: CityCode): HTMLElement {
  const wrap = document.createElement("div");
  wrap.setAttribute("data-ezi", "budget-range-bar");
  // Desktop-rail defaults are intentionally spartan (no card background, no
  // border, minimal padding) so the bar visually echoes the persona pill
  // next to it — both controls look like bare text-on-map in the top-right
  // rail. Mobile takes over via !important overrides in main-simple.ts
  // (filter-page slot) to re-add a card, bigger fonts, and touch-size
  // handles; both forms share this single DOM structure.
  wrap.style.cssText = `
    display:inline-flex;flex-direction:column;gap:4px;
    padding:0;
    background:transparent;
    border:0;
    border-radius:12px;
    font:600 11px/1 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
    letter-spacing:0.02em;
    color:#e0e0e0;
    min-width:220px;max-width:280px;
    box-sizing:border-box;
    user-select:none;
  `;

  /* ---------- Header row: label + current range --------------------- */
  const header = document.createElement("div");
  header.style.cssText = `
    display:flex;align-items:baseline;justify-content:space-between;
    gap:10px;width:100%;padding:0 2px;
  `;
  const title = document.createElement("span");
  title.textContent = "Budget";
  title.style.cssText = `
    color:#8a8a8a;font-size:9.5px;letter-spacing:0.10em;
    text-transform:uppercase;font-weight:700;line-height:1;
  `;
  const rangeTxt = document.createElement("span");
  rangeTxt.style.cssText = `
    color:#FFE6B0;font-size:11.5px;font-weight:700;letter-spacing:0;
    white-space:nowrap;line-height:1;
  `;
  header.appendChild(title);
  header.appendChild(rangeTxt);

  /* ---------- Bar container with the slider ------------------------- */
  const bar = document.createElement("div");
  bar.style.cssText = `
    position:relative;width:100%;height:22px;
    display:flex;align-items:center;
    padding:0 9px;box-sizing:border-box;
    touch-action:none;
  `;

  // Track (the rail)
  const track = document.createElement("div");
  track.style.cssText = `
    position:relative;width:100%;height:4px;
    background:rgba(255,255,255,0.12);
    border-radius:999px;
  `;

  // Amber fill between the two handles.
  const fill = document.createElement("div");
  fill.style.cssText = `
    position:absolute;top:0;bottom:0;
    background:linear-gradient(90deg,#FFD37E,#FFB85C);
    border-radius:999px;pointer-events:none;
  `;
  track.appendChild(fill);

  // Tick container — one tiny dot per snap stop, so the discreteness reads.
  const ticks = document.createElement("div");
  ticks.setAttribute("aria-hidden", "true");
  ticks.style.cssText = `
    position:absolute;inset:0;pointer-events:none;
  `;
  track.appendChild(ticks);

  bar.appendChild(track);

  // Handles
  const mkHandle = (kind: "min" | "max"): HTMLButtonElement => {
    const h = document.createElement("button");
    h.type = "button";
    h.setAttribute("role", "slider");
    h.setAttribute(
      "aria-label",
      kind === "min" ? "Minimum budget" : "Maximum budget"
    );
    h.dataset["kind"] = kind;
    h.style.cssText = `
      position:absolute;top:50%;left:0;
      transform:translate(-50%,-50%);
      width:16px;height:16px;border-radius:50%;
      background:#fff;
      border:2px solid #FFB85C;
      box-shadow:0 2px 6px rgba(0,0,0,0.45);
      cursor:grab;padding:0;outline:none;
      transition:box-shadow 120ms ease,transform 80ms ease;
      z-index:${kind === "min" ? 2 : 3};
      touch-action:none;
    `;
    // Invisible hitbox extension — bigger tap target than visible circle.
    // Done via an inner absolutely-positioned pseudo element trick: a child
    // <span> with `inset:-12px` catches pointer events outside the circle.
    const hit = document.createElement("span");
    hit.setAttribute("aria-hidden", "true");
    hit.style.cssText = `
      position:absolute;inset:-14px;display:block;
      border-radius:50%;
    `;
    h.appendChild(hit);
    return h;
  };
  const minH = mkHandle("min");
  const maxH = mkHandle("max");
  track.appendChild(minH);
  track.appendChild(maxH);

  wrap.appendChild(header);
  wrap.appendChild(bar);

  /* ---------- Reactive state ---------------------------------------- */

  let stops: number[] = [];
  let minIdx = 0;
  let maxIdx = 1;

  const ctx = (): BudgetFormatCtx => ({
    city,
    mode: getPurchaseMode(),
    type: getPropertyType(),
  });

  function renderTicks(total: number): void {
    if (ticks.childElementCount === total + 1) return; // already built
    ticks.innerHTML = "";
    for (let i = 0; i <= total; i++) {
      const t = document.createElement("span");
      const pct = (i / total) * 100;
      t.style.cssText = `
        position:absolute;top:50%;left:${pct}%;
        transform:translate(-50%,-50%);
        width:2px;height:2px;border-radius:50%;
        background:rgba(255,255,255,0.25);
      `;
      ticks.appendChild(t);
    }
  }

  function paint(): void {
    const c = ctx();
    stops = getStops(c);
    const total = stops.length - 1;
    // Clamp after context shift.
    minIdx = Math.min(Math.max(0, minIdx), total - 1);
    maxIdx = Math.min(Math.max(minIdx + 1, maxIdx), total);

    renderTicks(total);

    const minPct = (minIdx / total) * 100;
    const maxPct = (maxIdx / total) * 100;
    minH.style.left = `${minPct}%`;
    maxH.style.left = `${maxPct}%`;
    fill.style.left = `${minPct}%`;
    fill.style.right = `${100 - maxPct}%`;

    const loVal = stops[minIdx];
    const hiVal = stops[maxIdx];
    const atLastStop = maxIdx === total;

    // ARIA
    minH.setAttribute("aria-valuemin", String(loVal ? 0 : 0));
    minH.setAttribute("aria-valuemax", String(maxIdx - 1));
    minH.setAttribute("aria-valuenow", String(minIdx));
    minH.setAttribute("aria-valuetext", formatStop(c, loVal));
    maxH.setAttribute("aria-valuemin", String(minIdx + 1));
    maxH.setAttribute("aria-valuemax", String(total));
    maxH.setAttribute("aria-valuenow", String(maxIdx));
    maxH.setAttribute("aria-valuetext", formatMax(c, hiVal, atLastStop));

    // Header range text
    const isAny = minIdx === 0 && maxIdx === total;
    if (isAny) {
      rangeTxt.textContent = "Any";
      rangeTxt.style.color = "#9A9A9A";
    } else if (minIdx === 0) {
      rangeTxt.textContent = `Up to ${formatMax(c, hiVal, atLastStop)}`;
      rangeTxt.style.color = "#FFE6B0";
    } else {
      rangeTxt.textContent =
        `${formatStop(c, loVal)} – ${formatMax(c, hiVal, atLastStop)}`;
      rangeTxt.style.color = "#FFE6B0";
    }
  }

  function syncFromBucket(): void {
    const c = ctx();
    const { minIdx: mi, maxIdx: ma } = indicesForBucket(c, getBudgetBucket());
    minIdx = mi;
    maxIdx = ma;
    paint();
  }

  /**
   * True while we're inside our own commit() → setBudgetBucket() → subscriber
   * fan-out. Ensures the `subscribeBudgetBucket` callback doesn't re-snap
   * handles the user just dragged. Only EXTERNAL bucket changes (none today,
   * but future-proof — e.g., a Reset button that clears to "any") cause a
   * handle re-sync.
   */
  let internalCommit = false;

  function commit(): void {
    const c = ctx();
    const loVal = stops[minIdx];
    const hiVal = stops[maxIdx];
    const bucket = bestBucketForRange(c, loVal, hiVal);
    internalCommit = true;
    try {
      setBudgetBucket(bucket, city);
      // Publish the exact numeric window the user dragged to. When the bar
      // is at "any" (spans the full scale) we clear the range so the scorer
      // falls back to the legacy bucket path — a user who hasn't engaged
      // budget at all shouldn't get any hidden ceiling-preference logic.
      if (bucket === "any") {
        setUserBudgetRange(null);
      } else {
        setUserBudgetRange({ lo: loVal, hi: hiVal });
      }
    } finally {
      internalCommit = false;
    }
    // Paint to reflect handle positions. setBudgetBucket() is a no-op when
    // the bucket didn't actually change — so a bare `paint()` is necessary
    // to update the fill + labels for "same bucket, different handle stop"
    // moves (e.g., nudging min handle within the current bucket's range).
    paint();
  }

  /* ---------- Pointer drag ------------------------------------------ */

  function startDrag(e: PointerEvent, handle: HTMLButtonElement): void {
    e.preventDefault();
    e.stopPropagation();
    try { handle.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    handle.style.cursor = "grabbing";
    handle.style.boxShadow =
      "0 0 0 6px rgba(255,211,126,0.22),0 2px 6px rgba(0,0,0,0.45)";
    handle.style.transform = "translate(-50%,-50%) scale(1.12)";

    const kind = handle.dataset["kind"] as "min" | "max";
    const trackRect = track.getBoundingClientRect();
    const total = stops.length - 1;

    const pointerToIdx = (clientX: number): number => {
      if (trackRect.width <= 0) return 0;
      const rel = (clientX - trackRect.left) / trackRect.width;
      const raw = rel * total;
      return Math.round(Math.min(total, Math.max(0, raw)));
    };

    const onMove = (ev: PointerEvent): void => {
      const idx = pointerToIdx(ev.clientX);
      if (kind === "min") {
        minIdx = Math.min(idx, maxIdx - 1);
      } else {
        maxIdx = Math.max(idx, minIdx + 1);
      }
      paint();
    };
    const onEnd = (ev: PointerEvent): void => {
      try { handle.releasePointerCapture(ev.pointerId); } catch { /* ignore */ }
      handle.style.cursor = "grab";
      handle.style.boxShadow = "0 2px 6px rgba(0,0,0,0.45)";
      handle.style.transform = "translate(-50%,-50%) scale(1)";
      handle.removeEventListener("pointermove", onMove);
      handle.removeEventListener("pointerup", onEnd);
      handle.removeEventListener("pointercancel", onEnd);
      commit();
    };

    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", onEnd);
    handle.addEventListener("pointercancel", onEnd);
  }

  minH.addEventListener("pointerdown", (e) => startDrag(e, minH));
  maxH.addEventListener("pointerdown", (e) => startDrag(e, maxH));

  /* ---------- Click on the bar track jumps the nearest handle ------- */
  //
  // Power-user affordance: clicking the rail moves whichever handle is
  // closer to the click point. Matches most native range inputs. On
  // touch devices this is redundant with dragging, but costs nothing.
  bar.addEventListener("pointerdown", (e) => {
    // Ignore clicks that started on a handle (those have their own drag).
    if (e.target === minH || e.target === maxH) return;
    if (
      (e.target as HTMLElement).parentElement === minH ||
      (e.target as HTMLElement).parentElement === maxH
    ) return;
    const total = stops.length - 1;
    const rect = track.getBoundingClientRect();
    if (rect.width <= 0) return;
    const rel = (e.clientX - rect.left) / rect.width;
    const idx = Math.round(Math.min(total, Math.max(0, rel * total)));
    // Pick the handle that's closer.
    const distMin = Math.abs(idx - minIdx);
    const distMax = Math.abs(idx - maxIdx);
    if (distMin <= distMax) {
      minIdx = Math.min(idx, maxIdx - 1);
    } else {
      maxIdx = Math.max(idx, minIdx + 1);
    }
    paint();
    commit();
  });

  /* ---------- Keyboard ---------------------------------------------- */

  function step(handle: HTMLButtonElement, dir: -1 | 1): void {
    const kind = handle.dataset["kind"] as "min" | "max";
    if (kind === "min") {
      minIdx = Math.min(Math.max(0, minIdx + dir), maxIdx - 1);
    } else {
      maxIdx = Math.max(Math.min(stops.length - 1, maxIdx + dir), minIdx + 1);
    }
    paint();
    commit();
  }

  for (const h of [minH, maxH]) {
    h.addEventListener("keydown", (e) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
        e.preventDefault();
        step(h, -1);
      } else if (e.key === "ArrowRight" || e.key === "ArrowUp") {
        e.preventDefault();
        step(h, +1);
      } else if (e.key === "Home") {
        e.preventDefault();
        if (h === minH) minIdx = 0;
        else maxIdx = minIdx + 1;
        paint(); commit();
      } else if (e.key === "End") {
        e.preventDefault();
        if (h === minH) minIdx = maxIdx - 1;
        else maxIdx = stops.length - 1;
        paint(); commit();
      }
    });
    h.addEventListener("focus", () => {
      h.style.boxShadow =
        "0 0 0 3px rgba(255,211,126,0.42),0 2px 6px rgba(0,0,0,0.45)";
    });
    h.addEventListener("blur", () => {
      h.style.boxShadow = "0 2px 6px rgba(0,0,0,0.45)";
    });
    h.addEventListener("mouseenter", () => {
      if (document.activeElement !== h) {
        h.style.boxShadow =
          "0 0 0 4px rgba(255,211,126,0.22),0 2px 6px rgba(0,0,0,0.45)";
      }
    });
    h.addEventListener("mouseleave", () => {
      if (document.activeElement !== h) {
        h.style.boxShadow = "0 2px 6px rgba(0,0,0,0.45)";
      }
    });
  }

  /* ---------- Context subscriptions --------------------------------- */

  // Bucket changes coming from OURSELVES (commit() → setBudgetBucket)
  // should NOT re-snap handles — the user just moved them to a specific
  // stop set, snapping back would feel jarring (especially when min+max
  // don't sit exactly on a bucket's [lo,hi] and the "best fit" bucket is
  // narrower than the visible range). The internalCommit flag is raised
  // for the duration of our own commit(); any external bucket write
  // (e.g. future Reset-to-Any) still triggers a re-sync.
  subscribeBudgetBucket(() => {
    if (internalCommit) return;
    syncFromBucket();
  });
  // Mode/Type changes DO need a re-sync: the stop set shifts (Buy ↔ Rent
  // uses different ranges, House ↔ Apartment too), so we re-anchor the
  // handles to whatever stops now represent the persisted bucket.
  subscribePurchaseMode(() => syncFromBucket());
  subscribePropertyType(() => syncFromBucket());

  // Paint once after insertion so handle positions are correct. We defer
  // one frame because `left:%` requires the element to have a measurable
  // width; in some flexbox layouts the track is zero-width at insertion
  // time and only settles after the parent's first layout pass.
  syncFromBucket();
  requestAnimationFrame(() => paint());

  return wrap;
}
